import { BookingRentalMode } from '../interfaces/heavyDutyMachinery/IBooking';

/**
 * Client-side mirror of the backend's MockInsuranceProvider -- used ONLY to
 * show a live estimate in the checkout price breakdown before submitting.
 * The real, charged InsuranceCost is always fetched fresh server-side (via
 * IInsuranceProvider.GetQuoteAsync) in BookingController.CreateBooking, never
 * trusted from the client -- this never needs to match to the cent, just be
 * close enough not to surprise the guest at payment. Same placeholder
 * formula as the backend (0.4% of the insured value per rental day, 25€
 * minimum) -- see MockInsuranceProvider's own doc comment for why it's a
 * stub, and Booking.InsuranceCost's own doc comment for why it's persisted
 * rather than re-derived once the real quote is fetched.
 */
const DAILY_RATE_PERCENT = 0.4;
const MINIMUM_PREMIUM = 25;

/** Same rentalMode switch as the backend's own ComputeBasePrice -- the only
 * base-price estimate this checkout computes client-side before submitting,
 * so mirrored here rather than adding a shared utility just the one caller
 * (this estimate) and one callee (this file) would use. */
export const estimateBasePrice = (
  rentalMode: BookingRentalMode,
  pricePerHour: number | undefined,
  pricePerNight: number | undefined,
  pricePerDayPass: number | undefined,
  startAt: string,
  endAt: string
): number => {
  const start = new Date(startAt);
  const end = new Date(endAt);
  switch (rentalMode) {
    case 'Hourly':
      return (pricePerHour ?? 0) * ((end.getTime() - start.getTime()) / 3600000);
    case 'Overnight': {
      const startDay = new Date(start.getFullYear(), start.getMonth(), start.getDate());
      const endDay = new Date(end.getFullYear(), end.getMonth(), end.getDate());
      // Whole calendar days between the two boundaries. Since endAt is
      // exclusive (see rentalPeriodHelper), this difference already IS the
      // number of days rented -- the 17th to the 18th stores [17th, 19th)
      // and prices 2 days, which is what the backend's own
      // BookingPriceCalculator.ComputeBasePrice arrives at from the same
      // two dates.
      const days = Math.round((endDay.getTime() - startDay.getTime()) / 86400000);
      return (pricePerNight ?? 0) * Math.max(1, days);
    }
    case 'DayPass':
      return pricePerDayPass ?? 0;
    default:
      return 0;
  }
};

/** Same rentalDays criterion the backend uses for both Platform (MockInsuranceProvider's own RentalDays input) and Host insurance (BookingController.CreateBooking's own PricePerDay * rentalDays) -- exported so both estimateInsuranceCost and a Host policy's own exact PricePerDay * rentalDays estimate share one definition. */
export const computeRentalDays = (startAt: string, endAt: string): number =>
  Math.max(1, Math.ceil((new Date(endAt).getTime() - new Date(startAt).getTime()) / 86400000));

export const estimateInsuranceCost = (insuredValue: number, startAt: string, endAt: string): number => {
  const rentalDays = computeRentalDays(startAt, endAt);
  const premium = insuredValue * (DAILY_RATE_PERCENT / 100) * rentalDays;
  return Math.max(MINIMUM_PREMIUM, premium);
};
